import type { LayoutPlan } from '../models/layout';
import type { LegalBlock } from '../models/ast';
import { htmlFromLayoutPlan } from './htmlFromLayoutPlan';

export type ChromiumFontRef = { family: string; url: string; weight?: number; style?: 'normal'|'italic' };
export type ChromiumDocMeta = { title?: string; author?: string; creator?: string; producer?: string; createdAt?: string; versionId?: string; versionHash?: string; planHash?: string };
export type ChromiumAiMeta = unknown; // pass-through JSON for AI insights/annotations

export interface ChromiumExportOptions {
  pageWidthPx: number;
  pageHeightPx: number;
  background?: string;
  pdfa?: boolean;
  fonts?: ChromiumFontRef[];
  docMeta?: ChromiumDocMeta;
  aiMeta?: ChromiumAiMeta;
}

export function getEdgedocsApiBase(): string {
  try {
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host === '127.0.0.1';
    if (isLocal) return 'http://localhost:3000';
    // In production, rely on same-origin reverse proxy
    return '';
  } catch {
    return '';
  }
}

export async function exportPdfViaChromium(plan: LayoutPlan, ast: LegalBlock[], opts: ChromiumExportOptions): Promise<Uint8Array> {
  const html = htmlFromLayoutPlan(plan, ast, { pageWidthPx: opts.pageWidthPx, pageHeightPx: opts.pageHeightPx, background: opts.background, fonts: opts.fonts });
  let res: Response;
  try {
    res = await fetch(`${getEdgedocsApiBase()}/api/dev/edgedocs/export/pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html, pdfa: !!opts.pdfa, pageWidthPx: opts.pageWidthPx, pageHeightPx: opts.pageHeightPx, fonts: opts.fonts || [], docMeta: opts.docMeta || {}, aiMeta: opts.aiMeta ?? null }),
    });
  } catch (e) {
    throw new Error(`Network error calling Chromium exporter: ${String(e)}`);
  }
  if (!res.ok) {
    // Attempt structured error parse
    try {
      const j = await res.json();
      throw new Error(`Chromium export failed (${res.status}): ${j.error || j.code || 'ERROR'} – ${j.message || 'Unknown'}`);
    } catch {
      throw new Error(`Chromium export failed: ${res.status} ${res.statusText}`);
    }
  }
  const blob = await res.blob();
  const arr = new Uint8Array(await blob.arrayBuffer());
  return arr;
}


